import React from "react";
import { useState, useRef } from 'react';
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from 'dayjs';

import { Button, ContentModal, Inline, ConfirmationModal} from "@digitalreality/ui";
import { PlayIcon, DocumentTasksIcon, InfoIcon } from "@digitalreality/ui-icons";
import { ErrorLayout } from "@digitalreality/ui";

import { OverridableStringUnion } from '@mui/types';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

import { useAppState } from "../../StateStore/Store";
import { importDailyData, importDailyTrackingData } from "../../Services/JobsService";
import { DBJobStatus } from "../../Models/ProjectJobsStatus";
import { getProjectJobsStatus } from "../../StateStore/ProjectJobsStatusModal/ProjectJobsStatusModalActionMiddleware";

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { solarizedlight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import updateLocale from 'dayjs/plugin/updateLocale'
import utc from 'dayjs/plugin/utc';

const ClientJobsModal = function ( {showModal, setShowModalState}: any) {
    const JobType_SnowHeight1Day = 9;
    const JobType_SnowHeight3Days = 10;
    const JobType_SnowHeight7Days = 11;
    const JobType_ConXVehicleTracking = 12;

    // These mirror the JobStatus enum in database-helper's proto/shared.proto. They are
    // named rather than written inline because the enum has been renumbered before, and
    // bare numbers gave no hint that this file had to be updated with it. JobStatusUnknown
    // (0) is left out on purpose: it is handled by the default branch below.
    const JobStatus_JobStatusInProgress = 1;
    const JobStatus_JobStatusCompleted = 2;
    const JobStatus_JobStatusFailed = 3;
    const JobStatus_JobStatusCompletedWithErrors = 4;
    const JobStatus_JobStatusTimedOut = 5;

    const customStyle = {
        ...solarizedlight,
        'pre[class*="language-"]': {
          ...solarizedlight['pre[class*="language-"]'],
          background: '#ffffff11', // Custom background color
        },
        'code[class*="language-"]': {
          ...solarizedlight['code[class*="language-"]'],
          background: '#00000000', // Custom background color
        },
      };

    interface DayJobStatus {
        name: string;
        startDate: string;
        endDate: string;
        status: number;
        comment: string | null;
    }

    const { t } = useTranslation();
    const { state, dispatch } = useAppState();
    const handleModalClose = () => { setShowModalState(false);};
    const initialValue = dayjs(null);
    const refreshIntervalMillisec = 5000;
    const today = dayjs();

    const [highlightedDays, setHighlightedDays] = React.useState([1, 2, 15]);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedJobIndex, setSelectedJobIndex] = useState<number>(0);
    const [selectedJobComment, setselectedJobComment] = useState<string>("");
    const [dayJobsStatus, setValue] = React.useState<DayJobStatus[] | null>(null);
    const [showRunJobConfirmModal, setShowRunJobConfirmModal] = useState(false);
    const [showJobInfoModal, setShowJobInfoModal] = useState(false);
    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
    
    const StyledBadge = styled(Badge)(({ theme }) => ({'& .MuiBadge-badge': {right: 20, top: 6, border: `0px solid ${theme.palette.background.paper}`, padding: '0px 6px'}}));

    // Set the week starting with Monday and not Sunday
    dayjs.extend(updateLocale)
    dayjs.updateLocale('en', {
        weekStart: 1,
    })

    const onRunJobClick = (index: number) => {
        setSelectedJobIndex(index);
        setShowRunJobConfirmModal(true);
    };

    const onOpenJobInfoClick = (index: number) => {
        const jobId = state.projectJobsStatusModal.data?.jobs[index].id;
        var jobHistoryComment = "Unknown";

        // Check if the job was already processed
        if (state.projectJobsStatusModal.data?.jobshistory != undefined) {
            for (const jobHistory of state.projectJobsStatusModal.data?.jobshistory) {
                if (jobHistory.enddate?.value != null && jobHistory.startdate?.value != null) {
                    const endDate = dayjs(jobHistory.enddate?.value.seconds * 1000);
                    if (jobId == jobHistory.jobid && endDate.date() == selectedDate?.date() && endDate.month() == selectedDate?.month() && endDate.year() == selectedDate?.year()){
                        jobHistoryComment = jobHistory.comment?.value ?? "Unknown";
                    }
                }
            }
        }

        var formattedJson = "Unknown";
        if (jobHistoryComment != "Unknown") {
            const json = JSON.parse(jobHistoryComment);
            formattedJson = JSON.stringify(json, null, 2);
        }
        
        setselectedJobComment(formattedJson);
        setShowJobInfoModal(true);
    };
      
    function ServerDay(props: PickersDayProps<Dayjs> & { highlightedDays?: number[] }) {
        const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;
        const isSelected = !props.outsideCurrentMonth && highlightedDays.indexOf(props.day.date()) >= 0;
        const isValid = !props.outsideCurrentMonth;

        var hasFailedJobs = false;
        var hasCompletedJobs = false;
        if (state.projectJobsStatusModal.data?.jobshistory != undefined) {
            for (const jobHistory of state.projectJobsStatusModal.data?.jobshistory) {
                if (jobHistory.enddate?.value != null && jobHistory.startdate?.value != null) {
                    const endDate = dayjs.utc(jobHistory.enddate?.value.seconds * 1000);
                    if (endDate.date() == props.day.date() && endDate.month() == props.day.month() && endDate.year() == props.day.year()){
                        if (jobHistory.status == JobStatus_JobStatusFailed || jobHistory.status == JobStatus_JobStatusTimedOut) {
                            hasFailedJobs = true;
                        }
                        if (jobHistory.status == JobStatus_JobStatusCompleted) {
                            hasCompletedJobs = true;
                        }
                        if (jobHistory.status == JobStatus_JobStatusCompletedWithErrors) {
                            hasFailedJobs = true;
                            hasCompletedJobs = true;
                        }
                    }
                }
            }
        }

        var color: OverridableStringUnion<'primary' | 'secondary' | 'default' | 'error' | 'info' | 'success' | 'warning'> = 'default';
        if (hasCompletedJobs) {
            color = 'success';
        }
        if (hasFailedJobs) {
            color = 'error';
        }
        if (hasCompletedJobs && hasFailedJobs) {
            color = 'warning';
        }
    
        return (
            <StyledBadge
                key={props.day.toString()}
                overlap="circular"
                variant="dot"
                badgeContent={isSelected ? '' : ''}
                color={color}
                invisible={isValid ? false : true}>
                <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
            </StyledBadge>
        );
    }

    const fetchHighlightedDays = (date: Dayjs) => {
        const month = date.month();
        const year = date.year();
        if (state.projectJobsStatusModal.data != null && state.projectJobsStatusModal.data.jobshistory != null) {

            const daysToHighlight = state.projectJobsStatusModal.data.jobshistory
            .map(job => dayjs.unix(job.enddate?.value.seconds ?? 0)) // Convert to Dayjs object
            .filter(d => d.month() === month && d.year() === year) // Filter by month and year
            .map(d => d.date()); // Get the day of the month

            setHighlightedDays(daysToHighlight);
        }
    };
      
    const fetchData = () => {
        console.log("Fetching data...");
        getProjectJobsStatus(state.projectJobsStatusModal.projectId)(dispatch);
    };

    const startInterval = () => {
        refreshIntervalRef.current = setInterval(fetchData, refreshIntervalMillisec);
    };

    // Function to stop the interval
    const stopInterval = () => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current); // Clear the interval if it exists
            refreshIntervalRef.current = null; // Reset intervalRef.current after clearing
        }
    };

    React.useEffect(() => {
        if (showModal == true) {
            if (state.projectJobsStatusModal.loaded == true) {
                setHighlightedDays([]);
                fetchHighlightedDays(selectedDate);
                refreshCalendarDisplay(selectedDate);
            }

            startInterval(); // Start interval on mount
        }

        // Cleanup function to stop interval on component unmount or re-render
        return () => {
            stopInterval(); // Stop interval on unmount
        };
    }, [state.projectJobsStatusModal.loaded]);
    
    const handleMonthChange = (date: Dayjs) => {
        setHighlightedDays([]);
        fetchHighlightedDays(date);
    };

    const onConfirmRunJob = () => {
        setShowRunJobConfirmModal(false);
        runJob(selectedJobIndex);
    }

    const runJob = (index: number) => {
        const jobId = state.projectJobsStatusModal.data?.jobs[index].id;
        const jobType = state.projectJobsStatusModal.data?.jobs[index].jobType;
        const jobCronexpression: string = state.projectJobsStatusModal.data?.jobs[index].cronExpression?.value ?? '0 0 6 * * *';
        var jobHistoryId = 0;

        // Check if the job was already processed
        if (state.projectJobsStatusModal.data?.jobshistory != undefined) {
            for (const jobHistory of state.projectJobsStatusModal.data?.jobshistory) {
                if (jobHistory.enddate?.value != null && jobHistory.startdate?.value != null) {
                    const endDate = dayjs(jobHistory.enddate?.value.seconds * 1000);
                    if (jobId == jobHistory.jobid && endDate.date() == selectedDate?.date() && endDate.month() == selectedDate?.month() && endDate.year() == selectedDate?.year()){
                        jobHistoryId = jobHistory.id;
                    }
                }
            }
        }

        // We need to extract the time from the cron expression and apply it to the selected date.
        const [seconds, minute, hour] = jobCronexpression.split(' ');
        const numberHour = Number(hour);
        const numberMinute = Number(minute);

        var resultDate: Dayjs = dayjs.utc(selectedDate).hour(23).minute(59).second(0).millisecond(0);
        var endDateISO = resultDate.toISOString();

        if (!isNaN(numberHour) && Number.isInteger(numberHour) && !isNaN(numberMinute) && Number.isInteger(numberMinute)) {
            resultDate = dayjs.utc(selectedDate).hour(numberHour).minute(numberMinute).second(0).millisecond(0);
            endDateISO = resultDate.toISOString();
        }

        // Import the data
        switch (jobType) {
            case JobType_SnowHeight1Day: {
                const startDateISO = resultDate.subtract(1, 'day').toISOString();
                importDailyData(state.projectJobsStatusModal.projectId, jobId, jobHistoryId, startDateISO, endDateISO); 
            }
            break;
            case JobType_SnowHeight3Days: {
                const startDateISO = resultDate.subtract(3, 'day').toISOString();
                importDailyData(state.projectJobsStatusModal.projectId, jobId, jobHistoryId, startDateISO, endDateISO); 
            }
            break;
            case JobType_SnowHeight7Days: {
                const startDateISO = resultDate.subtract(7, 'day').toISOString();
                importDailyData(state.projectJobsStatusModal.projectId, jobId, jobHistoryId, startDateISO, endDateISO); 
            }
            break;
            case JobType_ConXVehicleTracking: {
                const startDateISO = resultDate.subtract(1, 'day').toISOString();
                importDailyTrackingData(state.projectJobsStatusModal.projectId, jobId, jobHistoryId, startDateISO, endDateISO);
            }
            break;
        }

        // Add a fake item to our list until the next refresh cycle is triggered
        const newJobHistoryItem: DBJobStatus = {
            id: 0,
            creationdate: null,
            jobid: jobId? jobId : 0,
            startdate: {value: {seconds: resultDate.subtract(1, 'day').unix(), nanos: 0}},
            enddate: {value: {seconds: resultDate.unix(), nanos: 0}},
            duration: null,
            status: JobStatus_JobStatusInProgress,
            comment: null
        };
        state.projectJobsStatusModal.data?.jobshistory.push(newJobHistoryItem);

        // Refresh the display
        refreshCalendarDisplay(selectedDate);
    };

    // The display is based on UTC time to avoid timezone issues. Adjustments can be made by using this variable: projectOffset * 1000 * 60 * 60
    const refreshCalendarDisplay = (date: Dayjs | null) => {
        const dayJobsStatus: DayJobStatus[] = [];
        const projectOffset = state.projectJobsStatusModal.projectTimezoneOffset;
        if (state.projectJobsStatusModal.data?.jobs != undefined) {

            for (const job of state.projectJobsStatusModal.data?.jobs) {
                var status = 0;
                var startDateString = "";
                var endDateString = "";
                var comment = null;
                for (const jobHistory of state.projectJobsStatusModal.data?.jobshistory) {
                    if (jobHistory.enddate?.value != null && jobHistory.startdate?.value != null) {
                        const startDate = dayjs.utc(jobHistory.startdate?.value.seconds * 1000 );
                        const endDate = dayjs.utc(jobHistory.enddate?.value.seconds * 1000);
                        if (job.id == jobHistory.jobid && endDate.date() == date?.date() && endDate.month() == date?.month() && endDate.year() == date?.year()){
                            status = jobHistory.status;
                            startDateString = startDate.format('DD.MM.YYYY HH:mm');
                            endDateString = endDate.format('DD.MM.YYYY HH:mm');
                            comment = jobHistory.comment?.value ?? null;
                        }
                    }
                }

                const newJobStatus: DayJobStatus = {
                    name: job.name,
                    startDate: startDateString,
                    endDate: endDateString,
                    status: status,
                    comment: comment
                };
                dayJobsStatus.push(newJobStatus);
            }
        }

        setValue(dayJobsStatus);
    }

    const onChangeDate = (date: Dayjs | null) => {
        refreshCalendarDisplay(date);
        setSelectedDate(dayjs(date));
    }

    const getJobItemDisplay = (index: number, status: number, comment: string | null) => {

        switch (status) {
            case JobStatus_JobStatusInProgress:
                return (
                    <ListItem alignItems="flex-start" sx={{ paddingTop: "0px" }}>
                        <ListItemText sx={{ width: "350px" }}
                            primary="Run status:"
                            secondary={<Typography style={{ fontSize: '12px', color: "#ebb434" }}>In Progress</Typography>}/>
                    </ListItem>
                );
            case JobStatus_JobStatusCompleted:
                return (
                    <ListItem alignItems="flex-start" sx={{ paddingTop: "0px" }}>
                        <ListItemText sx={{ width: "350px" }}
                            primary="Run status:"
                            secondary={<Typography style={{ fontSize: '12px', color: "#648F35" }}>Completed</Typography>}/>
                            {comment !== null && (
                                <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onOpenJobInfoClick(index)}>
                                    <ListItemIcon sx={{ minWidth: "unset" }}><InfoIcon /></ListItemIcon>
                                </ListItemButton>
                            )}
                        <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onRunJobClick(index)}>
                            <ListItemIcon sx={{ minWidth: "unset" }}><PlayIcon /></ListItemIcon>
                        </ListItemButton>
                    </ListItem>
                );
            case JobStatus_JobStatusFailed:
                return (
                    <ListItem alignItems="flex-start" sx={{ paddingTop: "0px" }}>
                        <ListItemText sx={{ width: "350px" }}
                            primary="Run status:"
                            secondary={<Typography style={{ fontSize: '12px', color: "#F34E5B" }}>Failed</Typography>}/>
                            {comment !== null && (
                                <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onOpenJobInfoClick(index)}>
                                    <ListItemIcon sx={{ minWidth: "unset" }}><InfoIcon /></ListItemIcon>
                                </ListItemButton>
                            )}
                        <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onRunJobClick(index)}>
                            <ListItemIcon sx={{ minWidth: "unset" }}><PlayIcon /></ListItemIcon>
                        </ListItemButton>
                    </ListItem>
                );
            case JobStatus_JobStatusTimedOut:
                return (
                    <ListItem alignItems="flex-start" sx={{ paddingTop: "0px" }}>
                        <ListItemText sx={{ width: "350px" }}
                            primary="Run status:"
                            secondary={<Typography style={{ fontSize: '12px', color: "#F34E5B" }}>Timed out</Typography>}/>
                            {comment !== null && (
                                <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onOpenJobInfoClick(index)}>
                                    <ListItemIcon sx={{ minWidth: "unset" }}><InfoIcon /></ListItemIcon>
                                </ListItemButton>
                            )}
                        <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onRunJobClick(index)}>
                            <ListItemIcon sx={{ minWidth: "unset" }}><PlayIcon /></ListItemIcon>
                        </ListItemButton>
                    </ListItem>
                );
            case JobStatus_JobStatusCompletedWithErrors:
                return (
                    <ListItem alignItems="flex-start" sx={{ paddingTop: "0px" }}>
                        <ListItemText sx={{ width: "350px" }}
                            primary="Run status:"
                            secondary={<Typography style={{ fontSize: '12px', color: "#eba234" }}>Completed, no data</Typography>}/>
                            {comment !== null && (
                                <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onOpenJobInfoClick(index)}>
                                    <ListItemIcon sx={{ minWidth: "unset" }}><InfoIcon /></ListItemIcon>
                                </ListItemButton>
                            )}
                        <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onRunJobClick(index)}>
                            <ListItemIcon sx={{ minWidth: "unset" }}><PlayIcon /></ListItemIcon>
                        </ListItemButton>
                    </ListItem>
                );
            default:
                return (
                    <ListItem alignItems="flex-start" sx={{ paddingTop: "0px" }}>
                        <ListItemText sx={{ width: "350px" }}
                            primary="Status:"
                            secondary={<Typography style={{ fontSize: '12px', color: "#aaaaaa" }}>Not executed</Typography>}/>
                        <ListItemButton sx={{ justifyContent: "center", marginTop: "6px" }} onClick={() => onRunJobClick(index)}>
                            <ListItemIcon sx={{ minWidth: "unset" }}><PlayIcon /></ListItemIcon>
                        </ListItemButton>
                    </ListItem>
                );
        }
    };

    return (
        <ContentModal
            ModalActionsProps={{sx: {padding: "15px 15px 15px 15px" }}}
            ModalContentProps={{sx: {padding: "0px 10px 0px 10px" }}}
            variant="solid"
            title={t("Texts.Jobs")}
            size="extraLarge"
            open={showModal}
            onClose={handleModalClose}
            footerContent={
                <Inline justifyContent="flex-end" gap={3}>
                    <Button onClick={handleModalClose} variant="outlined">
                        {t("Texts.OK")}
                    </Button>
                </Inline>
            }>

            <Divider />

            {state.projectJobsStatusModal.errorCode ?
            (
                <ErrorLayout title="Something went wrong" text="Please try again later" sx={{ height: "350px"}}></ErrorLayout>
            ) :
            (
                <Stack
                    direction="row">
                    <DateCalendar
                        defaultValue={initialValue}
                        onMonthChange={handleMonthChange}
                        onChange={onChangeDate}
                        value={selectedDate}
                        renderLoading={() => <DayCalendarSkeleton />}
                        slots={{day: ServerDay}}
                        slotProps={{day: {highlightedDays} as any}}
                        maxDate={today}/>
                    <Stack
                        direction="column"
                        sx={{ width: "450px", height: "350px", maxHeight: "350px", overflowY: "auto" }}>
                        {dayJobsStatus?.map((item, index) => (
                            <List key={index} sx={{ paddingBottom: "0px", paddingTop: "0px" }}>
                                <ListItem alignItems="flex-start" sx={{ paddingBottom: "0px" }}>
                                    <ListItemText primary={<><Typography style={{ fontSize: '16px', fontWeight: "800" }}>{item.name}</Typography></>} secondary={<><Typography style={{ fontSize: '12px' }}><>{item.startDate} - {item.endDate}</></Typography></>}/>
                                </ListItem>
                                {getJobItemDisplay(index, item.status, item.comment)}
                                {index < dayJobsStatus.length - 1 && (<Divider variant="middle" component="li" />)}
                            </List>
                        ))}
                    </Stack>
                </Stack>
            )}

            <Divider />

            <ConfirmationModal
                title={"Are you sure you want to run the selected job ?"}
                size="medium"
                icon={null} 
                open={showRunJobConfirmModal}
                cancelButtonText={t("Texts.Cancel")}
                confirmButtonText={t("Texts.OK")}
                onCancel={() => setShowRunJobConfirmModal(false)}
                onConfirm={onConfirmRunJob}>
                {"The previous data will be deleted"}
            </ConfirmationModal>

            <ContentModal 
                title="" 
                size="extraLarge"
                open={showJobInfoModal} 
                onClose={() => setShowJobInfoModal(false)}>
                <Typography
                    component="pre"
                    style={{borderRadius: '10px', overflowX: 'auto'}}>
                    {}
                    <SyntaxHighlighter language="json" style={customStyle}>
                        {selectedJobComment}
                    </SyntaxHighlighter>
                </Typography>
            </ContentModal>
        </ContentModal>
    );
};
export default ClientJobsModal;
  